import React, { ChangeEvent, useCallback, useMemo } from 'react';
import {
  type MasonryEngineNode,
  type MasonryEngineNodeRendererProps,
  type BaseFormFieldNodeProperties,
} from '@leagueplatform/masonry-engine';
import { Select, SelectOptionsGroup } from '@leagueplatform/genesis-core';
import { MasonryDriverAction } from '#types/masonry-driver-node-actions.ts';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';
import { useInvokeAction } from '../../../hooks/use-invoke-action.hook';
import { MasonryFormFieldWrapper } from '../masonry-form-field-renderer/masonry-form-field-wrapper.component';

type MasonrySelectInputGroupedOption = {
  label: string;
  value: string;
  disabled?: boolean;
};

type MasonrySelectInputOption = {
  label: string;
  value?: string;
  disabled?: boolean;
  options?: MasonrySelectInputGroupedOption[];
};

type MasonrySelectInputNodeProperties = BaseFormFieldNodeProperties<string> & {
  disabled?: boolean;
  ariaDisabled?: boolean;
  options: MasonrySelectInputOption[];
  placeholder?: string;
};

type MasonrySelectInputNodeAction = {
  onChange?: MasonryDriverAction;
};

export type MasonrySelectInputNode = MasonryEngineNode<
  'selectInput',
  MasonrySelectInputNodeProperties,
  MasonrySelectInputNodeAction
>;

export type MasonrySelectInputRendererProps =
  MasonryEngineNodeRendererProps<MasonrySelectInputNode>;

export const MasonrySelectInputRenderer = ({
  css,
  formFieldProperties,
  value,
  placeholder,
  ariaDisabled,
  options,
  disabled,
  onChange,
  rootElementRef,
  className,
  engineClassName,
  locatorId,
}: MasonrySelectInputRendererProps) => {
  const { formFieldId, hint: fieldHint } = formFieldProperties;

  // Computed Props
  const computedValue = useMemo(() => {
    if (value?.length) return value;
    return '';
  }, [value]);

  // Field State
  const invokeAction = useInvokeAction();

  // Event Handlers
  const setNodeState = useCallback(
    async (event: ChangeEvent<HTMLSelectElement>) => {
      const state: Record<string, unknown> = {
        value: event.currentTarget.value,
      };

      if (
        formFieldProperties.inputStatus ||
        formFieldProperties.statusMessage
      ) {
        state.formFieldProperties = {
          ...formFieldProperties,
          inputStatus: undefined,
          statusMessage: undefined,
        };
      }

      await invokeAction({
        event,
        type: 'setNodeState',
        namespace: 'default',
        payload: { state },
      });

      if (onChange) invokeAction({ event, ...onChange });
    },
    [formFieldProperties, invokeAction, onChange],
  );

  const selectOptions = useMemo(
    () =>
      options.map(
        (
          {
            label,
            value: optValue = '',
            disabled: optDisabled,
            options: groupOptions,
          },
          index: number,
        ) => {
          if (groupOptions) {
            return {
              key: `${formFieldId}-${index}`,
              label,
              disabled: optDisabled,
              options: groupOptions.map((childOpt, childIndex: number) => ({
                key: `${formFieldId}-${index}${childIndex}`,
                value: childOpt.value,
                disabled: childOpt.disabled,
                label: childOpt.label,
              })),
            };
          }
          return {
            key: `${formFieldId}-${index}`,
            label,
            // Need to pass empty string for empty values
            // otherwise the select sets the label as the value
            value: optValue || '',
            disabled: optDisabled,
          };
        },
      ),
    [formFieldId, options],
  );

  return (
    <MasonryFormFieldWrapper
      formFieldProperties={formFieldProperties}
      hint={fieldHint}
      rootElementRef={rootElementRef}
      className={formatClassNames(engineClassName, className)}
      data-testid={locatorId}
    >
      <Select
        aria-disabled={ariaDisabled}
        placeholder={placeholder}
        name={formFieldId}
        value={computedValue}
        disabled={disabled}
        options={selectOptions as SelectOptionsGroup[]}
        onChange={setNodeState}
        css={css}
      />
    </MasonryFormFieldWrapper>
  );
};
